/**
 * AccountSaveSystem.ts
 * 
 * Manages player character save files in localStorage, shared between Main Client and Admin Console.
 * Storage Key: 'terra_anima_admin_accounts'
 */

import { EntityState, RaceId, CharacterAppearance, PersonalityProfile, StatBlock } from './Types';
import { ActivityLog } from './ActivityLog';
import { ApiClient } from './ApiClient';
import { Currency } from './Currency';

export interface SavedCharacterSlot {
  id: string;
  name: string;
  class: string;
  race?: RaceId;
  level: number;
  exp: number;
  gold: number;
  unlockedSlots?: number;
  appearance?: CharacterAppearance;
  personality?: PersonalityProfile;
  stats: {
    str: number;
    con: number;
    dex: number;
    qui: number;
    int: number;
    pie: number;
    cha: number;
  };
  inventory?: any[];
  quests?: any[];
  zoneId?: number;
  x?: number;
  y?: number;
}

export interface AccountDataStore {
  provider?: 'discord' | 'google' | 'local';
  email?: string;
  name?: string;
  avatarUrl?: string;
  slots: SavedCharacterSlot[];
  /** 다이아로 구매한 추가 슬롯 수 (기본 3칸 + extraSlots) */
  extraSlots?: number;
  /** 계정 보유 다이아 (보석) */
  gems?: number;
}

export const ADMIN_ACCOUNTS_STORAGE_KEY = 'terra_anima_admin_accounts';

export class AccountSaveSystem {
  public static getAccountKey(currentUser: { provider?: string; email?: string; userId?: string } | null): string {
    if (!currentUser || !currentUser.provider) return 'local_guest';
    if (currentUser.userId) {
      return `${currentUser.provider}_${currentUser.userId}`;
    }
    if (currentUser.email) {
      return `${currentUser.provider}_${currentUser.email}`;
    }
    return `${currentUser.provider}_user`;
  }

  public static getCharactersForAccount(accountKey: string): SavedCharacterSlot[] {
    const all = this.loadAllAccounts();
    return all[accountKey]?.slots || [];
  }

  /** 서버 API로부터 캐릭터 목록을 가져와 로컬 캐시를 갱신한다. (로그인 후 호출) */
  public static async syncCharactersFromServer(accountKey: string): Promise<SavedCharacterSlot[]> {
    if (!ApiClient.getToken()) return this.getCharactersForAccount(accountKey);
    try {
      const serverChars = await ApiClient.getCharacters();
      const all = this.loadAllAccounts();
      if (!all[accountKey]) all[accountKey] = { slots: [] };

      // 서버에 캐릭터가 없고, 로컬에 이미 캐릭터가 있다면 자동으로 서버 DB로 1회 업로드 마이그레이션
      if (serverChars.length === 0 && all[accountKey].slots && all[accountKey].slots.length > 0) {
        console.log(`[AccountSaveSystem] Migrating ${all[accountKey].slots.length} local characters to server DB...`);
        const uploaded: SavedCharacterSlot[] = [];
        for (const localChar of all[accountKey].slots) {
          try {
            const created = await ApiClient.createCharacter(localChar);
            uploaded.push(created);
          } catch (upErr) {
            console.warn('[AccountSaveSystem] Failed to migrate local char to server', localChar.name, upErr);
            uploaded.push(localChar);
          }
        }
        all[accountKey].slots = uploaded;
        this.saveAllAccounts(all);
        return uploaded;
      }

      all[accountKey].slots = serverChars;
      this.saveAllAccounts(all);
      return serverChars;
    } catch (e) {
      console.warn('[AccountSaveSystem] Failed to sync characters from server', e);
      return this.getCharactersForAccount(accountKey);
    }
  }

  /** 기본 캐릭터 슬롯 수 (확장은 다이아로 구매) */
  public static readonly BASE_SLOTS = 3;
  public static readonly MAX_SLOTS = 9;
  public static readonly SLOT_EXPAND_GEM_COST = 100;

  public static getSlotLimit(accountKey: string): number {
    const extra = this.loadAllAccounts()[accountKey]?.extraSlots || 0;
    return Math.min(this.MAX_SLOTS, this.BASE_SLOTS + extra);
  }

  /** 슬롯 1칸 확장(다이아 차감은 호출 측에서 처리). 성공 시 true */
  public static expandSlot(accountKey: string): boolean {
    const all = this.loadAllAccounts();
    if (!all[accountKey]) all[accountKey] = { slots: [] };
    const cur = Math.min(this.MAX_SLOTS, this.BASE_SLOTS + (all[accountKey].extraSlots || 0));
    if (cur >= this.MAX_SLOTS) return false;
    all[accountKey].extraSlots = (all[accountKey].extraSlots || 0) + 1;
    this.saveAllAccounts(all);
    return true;
  }

  public static ensureAccountExists(currentUser: { provider?: string; email?: string; name?: string; avatarUrl?: string } | null): string {
    const accKey = this.getAccountKey(currentUser);
    const all = this.loadAllAccounts();

    if (!all[accKey]) {
      all[accKey] = {
        provider: (currentUser?.provider as any) || (accKey.includes('discord') ? 'discord' : accKey.includes('google') ? 'google' : 'local'),
        email: currentUser?.email || (accKey === 'discord_admin' ? 'admin@discord' : 'guest@local'),
        name: currentUser?.name || (accKey === 'discord_admin' ? '솔라리스 3세' : '게스트 모험가'),
        avatarUrl: currentUser?.avatarUrl || '',
        slots: [], // 신규 계정은 캐릭터 없음(0개)
      };
      this.saveAllAccounts(all);
      ActivityLog.log('ACCOUNT_FIRST_LOGIN', accKey, `계정 첫 접속 (${currentUser?.provider || 'local'})`);
      if (currentUser?.provider && currentUser.provider !== 'local') {
        ActivityLog.log('ACCOUNT_LINKED', accKey, `${currentUser.provider} 계정 연동 완료`);
      }
      console.log(`👤 [AccountSaveSystem] Registered new account in admin storage: ${accKey}`);
    } else {
      // 기존 계정 정보 동기화
      if (currentUser?.name) all[accKey].name = currentUser.name;
      if (currentUser?.email) all[accKey].email = currentUser.email;
      if (currentUser?.avatarUrl) all[accKey].avatarUrl = currentUser.avatarUrl;
      this.saveAllAccounts(all);
    }
    return accKey;
  }

  public static loadAllAccounts(): Record<string, AccountDataStore> {
    try {
      const raw = localStorage.getItem(ADMIN_ACCOUNTS_STORAGE_KEY);
      if (raw) {
        const data: Record<string, AccountDataStore> = JSON.parse(raw);
        return data;
      }
    } catch (e) {
      console.warn('Failed to parse saved accounts from storage', e);
    }
    return {};
  }

  public static saveAllAccounts(data: Record<string, AccountDataStore>): void {
    try {
      localStorage.setItem(ADMIN_ACCOUNTS_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save accounts to storage', e);
    }
  }

  /** 이미 사용 중인 캐릭터 이름인지 (모든 계정 기준, 대소문자/공백 무시) */
  public static isNameTaken(name: string): boolean {
    const target = name.trim().toLowerCase();
    if (!target) return false;
    const all = this.loadAllAccounts();
    for (const k of Object.keys(all)) {
      for (const sl of all[k]?.slots || []) {
        if ((sl.name || '').trim().toLowerCase() === target) return true;
      }
    }
    return false;
  }

  /** 관리자 페이지에서 설정한 '신규 캐릭터 시작 지급' (골드 + 아이템) */
  public static buildStarterPack(): { gold: number; items: any[] } {
    try {
      const raw = localStorage.getItem('terra_anima_starter_pack');
      if (raw) {
        const p = JSON.parse(raw);
        const items = (Array.isArray(p.items) ? p.items : [])
          .filter((it: any) => it && it.name)
          .slice(0, 20)
          .map((it: any, i: number) => ({
            id: `item_${Date.now().toString(36)}_${i}`,
            name: it.name,
            icon: it.icon || '📦',
            category: it.category || 'material',
            rarity: it.rarity || 'common',
            quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          }));
        return { gold: Math.max(0, parseInt(p.gold, 10) || 0), items };
      }
    } catch {
      /* ignore */
    }
    return { gold: Currency.toCopper({ gold: 100 }), items: [] };
  }

  public static async saveNewCharacter(
    accountKey: string,
    charData: {
      name: string;
      race: RaceId;
      stats: StatBlock;
      personality: PersonalityProfile;
      appearance: CharacterAppearance;
    }
  ): Promise<SavedCharacterSlot> {
    const all = this.loadAllAccounts();
    // 조회(getCharactersForAccount)와 동일한 계정에 저장하여, 새 캐릭터가 목록에서 사라지지 않게 함
    const existing = this.getCharactersForAccount(accountKey);
    for (const k of Object.keys(all)) {
      if (existing.length > 0 && all[k]?.slots?.[0]?.id === existing[0].id) {
        accountKey = k;
        break;
      }
    }
    if (!all[accountKey]) {
      all[accountKey] = { slots: [] };
    }

    const starter = this.buildStarterPack();
    const newSlot: SavedCharacterSlot = {
      id: 'char_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 6),
      name: charData.name,
      class: 'paladin', // Default base class motion
      race: charData.race,
      level: 1,
      exp: 0,
      gold: starter.gold,
      unlockedSlots: 20,
      stats: charData.stats,
      personality: charData.personality,
      appearance: charData.appearance,
      inventory: starter.items,
      quests: [
        { id: 'q1', title: '[튜토리얼] 왕국 안내원 브렌든과 대화하기', status: 'not_started', step: 0, maxStep: 1 }
      ],
      zoneId: 1,
      x: 80,
      y: 80,
    };

    all[accountKey].slots.push(newSlot);
    this.saveAllAccounts(all);
    ActivityLog.log('CHARACTER_CREATED', accountKey, '새 캐릭터 생성 완료', newSlot.name);
    console.log(`💾 [SaveSystem] Saved new character to account [${accountKey}]:`, newSlot);

    // 서버 API에 먼저 또는 병행 등록
    if (ApiClient.getToken()) {
      try {
        const srvChar = await ApiClient.createCharacter(newSlot);
        if (srvChar && srvChar.id) {
          // 서버에서 발급된 ID나 필드가 있다면 반영
          const idx = all[accountKey].slots.findIndex((s) => s.id === newSlot.id);
          if (idx !== -1) {
            all[accountKey].slots[idx] = srvChar;
            this.saveAllAccounts(all);
          }
          return srvChar;
        }
      } catch (e) {
        console.warn('[AccountSaveSystem] Server char create failed', e);
      }
    }

    return newSlot;
  }

  public static async updateCharacter(accountKey: string, slotId: string, updates: Partial<SavedCharacterSlot>): Promise<void> {
    const all = this.loadAllAccounts();
    const slots = all[accountKey]?.slots;
    if (!slots) return;

    const idx = slots.findIndex((s) => s.id === slotId);
    if (idx !== -1) {
      slots[idx] = { ...slots[idx], ...updates };
      this.saveAllAccounts(all);
    }

    // 서버 API에도 업데이트
    if (ApiClient.getToken()) {
      try {
        await ApiClient.updateCharacter(slotId, updates);
      } catch (e) {
        console.warn('[AccountSaveSystem] Server char update failed', e);
      }
    }
  }

  public static deleteAccount(accountKey: string): boolean {
    // 관리자 계정은 삭제 불가!
    if (accountKey.toLowerCase().includes('admin') || accountKey === 'discord_admin') {
      console.warn(`🛡️ [AccountSaveSystem] 관리자 계정(${accountKey})은 삭제할 수 없습니다.`);
      return false;
    }
    const all = this.loadAllAccounts();
    if (all[accountKey]) {
      delete all[accountKey];
      this.saveAllAccounts(all);
      console.log(`🗑️ [AccountSaveSystem] Account [${accountKey}] deleted successfully.`);
      return true;
    }
    return false;
  }

  public static async deleteCharacter(accountKey: string, slotId: string): Promise<void> {
    const all = this.loadAllAccounts();
    const slots = all[accountKey]?.slots;
    if (!slots) return;

    all[accountKey].slots = slots.filter((s) => s.id !== slotId);
    this.saveAllAccounts(all);

    // 서버 API에도 삭제 요청
    if (ApiClient.getToken()) {
      try {
        await ApiClient.deleteCharacter(slotId);
      } catch (e) {
        console.warn('[AccountSaveSystem] Server char delete failed', e);
      }
    }
  }
}
